import { Button, Col, message, Row, Space, Spin, Typography } from 'antd';
import { CaretSpinIcon } from 'components/atoms/CaretSpinIcon';
import { Loader } from 'components/atoms/Loader';
import { ActionControls } from 'components/molecules/ActionControls';
import { AddUserModal } from 'components/molecules/AddUserModal';
import { ProjectForm } from 'components/molecules/ProjectForm';
import { TasksList } from 'components/molecules/TaskList';
import { HeaderCard } from 'components/organisms/HeaderCard';
import { FormikProvider, useFormik } from 'formik';
import { projectPayload, ProjectTypePayload } from 'models/Project';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateProjectMutation } from 'redux/services/mrsGrouch/mrsGrouchApi';
import { useAppSelector } from 'redux/store';

export const CreateProjectPage = (): JSX.Element => {
  const [createProject] = useCreateProjectMutation();
  const { divisionId } = useParams();
  const nav = useNavigate();
  const { exit, user } = useAppSelector((state) => state.app);

  const formik = useFormik<ProjectTypePayload>({
    enableReinitialize: true,
    validationSchema: projectPayload,
    initialValues: {
      isActive: true,
      customerProjectId: '',
      description: '',
      projectType: '',
      isBudgetOnHold: false,
      owner: {
        fullName: user?.name ?? '',
        userId: user?.sub ?? ''
      },
      status: '',
      tasks: [],
      users: [],
      userDefinedFields: []
    },
    onSubmit: async (vals) => {
      try {
        if (!divisionId) return message.error('An error occured, the team has been notified');
        const response = await createProject({ divisionId, payload: vals }).unwrap();

        message.success(`${response.customerProjectId} has been successfully created`);
        if (exit) nav(`/${divisionId}/projects`);
        else nav(`/${divisionId}/projects/${response.id}`);
      } catch (err) {
        console.log(err);
        message.error((err as { data: { errorMessage: string } }).data.errorMessage);
      }
    }
  });

  const [isAddUsersOpen, setIsAddUsersOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(false);

  const handleAddUser = (user: NonNullable<ProjectTypePayload['users']>[number]): void => {
    // append and dedupe by userId
    const existing = formik.values.users || [];

    if (existing.some((u) => u.userId === user.userId)) return;

    formik.setFieldValue('users', [...existing, user]);
  };

  // Auto-expand users chevron if there are users
  useEffect(() => {
    if ((formik.values.users?.length || 0) > 0) setShowUsers(true);
  }, [formik.values.users?.length]);

  const handleToggleShowUsers = (): void => setShowUsers((prev) => !prev);

  return (
    <Spin spinning={formik.isSubmitting} indicator={<Loader message="Creating Project" />}>
      <FormikProvider value={formik}>
        <Space style={{ width: '100%' }} direction="vertical">
          <Row justify="space-between">
            <Col>
              <Typography.Title style={{ padding: 0, margin: 0, fontWeight: 300 }} level={4}>
                Create Project
              </Typography.Title>
            </Col>
            <ActionControls />
          </Row>
          <HeaderCard title="General" headerColor="rgb(206, 213, 242)">
            <ProjectForm />
          </HeaderCard>
          <TasksList />
          {/* Users chevron section matching edit mode */}
          <Col span={24} style={{ marginBottom: showUsers ? 10 : 0 }}>
            <Row onClick={handleToggleShowUsers} style={{ padding: 15, background: 'rgba(24, 167, 153, 0.518)', borderRadius: 5, cursor: 'pointer' }} justify="space-between" align="middle">
              <Typography.Title style={{ fontWeight: 400, padding: 0, margin: 0, fontSize: 22 }} level={3}>
                Users {formik.values.users?.length ? `(${formik.values.users.length})` : ''}
              </Typography.Title>
              <Row onClick={(e): void => e.stopPropagation()}>
                <Col>
                  <Button onClick={(): void => setIsAddUsersOpen(true)}>Add Users</Button>
                </Col>
                <Col>
                  <Button style={{ width: 115 }} type="text" onClick={handleToggleShowUsers} icon={<CaretSpinIcon isOpen={showUsers} />}>
                    {showUsers ? 'Collapse' : 'Expand'}
                  </Button>
                </Col>
              </Row>
            </Row>
            {showUsers && (
              <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                {(formik.values.users || []).map((u) => (
                  <Col key={u.userId} span={8}>
                    <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                      <Typography.Text strong style={{ display: 'block' }}>
                        {u.fullName}
                      </Typography.Text>
                      {u.email && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {u.email}
                        </Typography.Text>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
          <AddUserModal isOpen={isAddUsersOpen} setIsOpen={setIsAddUsersOpen} mode="create" onUserAdded={handleAddUser} existingUsers={formik.values.users || []} />
        </Space>
      </FormikProvider>
    </Spin>
  );
};
